import * as React from 'react';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { useArtikelDetail } from 'api/artikelDetail';
import { Artikel, useArtikel } from 'api/artikel';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { formStyles } from 'styles/formStyles';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import {
    artikelGruppenByNrSelector,
    einheitenByCodeSelector,
    kalkulationsblaetterByBlattSelector,
} from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { KalkulationsblattByBlatt } from 'formComponents/lookups/KalkulationsblattByBlatt';
import { Kalkulationsblatt } from 'api/lookup';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { SavingState } from 'api/savingState';
import { updateArtikelDetailLieferant, useArtikelDetailLieferant } from 'api/artikelDetailLieferant';
import { TextField } from '@fluentui/react';
import {
    addArtikelDetailKalkulation,
    ArtikelDetailKalkulation,
    defaultArtikelDetailKalkulation,
    useArtikelDetailKalkulationList,
} from 'api/artikelDetailKalkulation';
import useArtikelCalculator from 'hooks/useArtikelCalculator';

const styles = mergeStyleSets({
    firstLine: {
        display: 'grid',
        gridTemplateColumns: '120px 200px 3fr 2fr',
        gridColumnGap: 10,
        marginBottom: 10,
    },
    newKalkulationGrid: {
        display: 'grid',
        gridTemplateColumns: '2fr 2fr 3fr',
        gridColumnGap: 10,
    },
    block: {
        border: '1px solid #CCC',
        padding: 10,
    },
    secondBlock: {
        marginTop: 10,
    },
    blockTitle: {
        fontSize: 14,
        fontWeight: 700,
        textAlign: 'center',
    },
    resultColumn: {
        display: 'grid',
        gridTemplateRows: '1fr 1fr',
        gridRowGap: 10,
    },
});

interface KalkulationData {
    initialized: boolean;
    ekAnzahlEinheiten: number;
    ek1: number;
    ekGesamt: number;
    ekProEh: number;
    ek1Ek2Diff: number;
    ek2: number;
    vkProz: number;
    vkGesamt: number;
    vkAnzahlEinheiten: number;
    vk: number;
    bruttoGew: number;
    einh1Ek2: number;
    einh1Ek2Factor: number;
    einh2Ek2: number;
    einh2Ek2Factor: number;
    einh3Ek2: number;
    einh3Ek2Factor: number;
    einh4Ek2: number;
    einh4Ek2Factor: number;
    einh1Vk: number;
    einh1VkFactor: number;
    einh2Vk: number;
    einh2VkFactor: number;
    einh3Vk: number;
    einh3VkFactor: number;
    einh4Vk: number;
    einh4VkFactor: number;
}

const defaultKalkulationData: KalkulationData = {
    initialized: false,
    ekAnzahlEinheiten: 0,
    ek1: 0,
    ekGesamt: 0,
    ekProEh: 0,
    ek1Ek2Diff: 0,
    ek2: 0,
    vkProz: 0,
    vkGesamt: 0,
    vkAnzahlEinheiten: 0,
    vk: 0,
    bruttoGew: 0,
    einh1Ek2: 0,
    einh1Ek2Factor: 0,
    einh2Ek2: 0,
    einh2Ek2Factor: 0,
    einh3Ek2: 0,
    einh3Ek2Factor: 0,
    einh4Ek2: 0,
    einh4Ek2Factor: 0,
    einh1Vk: 0,
    einh1VkFactor: 0,
    einh2Vk: 0,
    einh2VkFactor: 0,
    einh3Vk: 0,
    einh3VkFactor: 0,
    einh4Vk: 0,
    einh4VkFactor: 0,
};

interface ArtikelDetailKalkulationNewProps {
    artikelGruppeNr: number;
    artikelNr: string;
    lNr: number;
    onClose(refresh: boolean): void;
}

export const ArtikelDetailKalkulationNew: React.FC<ArtikelDetailKalkulationNewProps> = ({
    artikelGruppeNr,
    artikelNr,
    lNr,
    onClose,
}) => {
    const kalkulationsblaetterByBlatt = useSelector(kalkulationsblaetterByBlattSelector);
    const artikelGruppeByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const [kalkulationData, setKalkulationData] = React.useState<KalkulationData>(defaultKalkulationData);
    const artikelCalculator = useArtikelCalculator();

    const artikel = useArtikel({
        artikelNr,
        includeArchiv: true,
    });

    const kalkulationsblatt: Kalkulationsblatt | null = React.useMemo(
        () => (artikel.data?.kalkBlatt ? kalkulationsblaetterByBlatt[artikel.data.kalkBlatt] || null : null),
        [artikel, kalkulationsblaetterByBlatt],
    );

    const artikelDetail = useArtikelDetail({
        artikelGruppeNr,
        artikelNr,
        includeArchiv: true,
    });

    const artikelDetailLieferant = useArtikelDetailLieferant({
        artikelGruppeNr,
        artikelNr,
        lNr,
        withLieferant: true,
    });

    useArtikelDetailKalkulationList(
        {
            artikelGruppeNr,
            artikelNr,
            lNr,
            onlyLatest: true,
        },
        {
            allowCaching: false,
            staleTime: 0,
            // trigger when everything we depend on is loaded
            enabled: !!artikel.data && !!artikelDetail.data && !!artikelDetailLieferant.data,
            onSuccess: (data) => {
                if (kalkulationData.initialized) {
                    return;
                }

                const oldKalk = data.find((k) => k.eh === artikel.data?.einheit);

                if (oldKalk) {
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    setValuesAndRecalculate({
                        ...kalkulationData,
                        initialized: true,
                        ekAnzahlEinheiten: 1, // artikel.data?.einhProPal || 0,
                        ek1: oldKalk.ek1,
                        ek2: oldKalk.ek2,
                        vkProz: Math.round((oldKalk.vk / (100 / oldKalk.ek2)) * 10000) / 10000,
                        vkAnzahlEinheiten: 1, // artikel.data?.einhProPal || 0,
                        vk: oldKalk.vk,
                    });
                } else {
                    let vkProz = 0;
                    let ek2 = 0;
                    let vk = 0;

                    if (artikelDetailLieferant.data) {
                        ek2 = artikelDetailLieferant.data.ekPreis;
                        vk = artikelDetailLieferant.data.vkPreis;

                        if (ek2 > 0) {
                            vkProz = Math.round((vk / (100 / ek2)) * 10000) / 10000;
                        }
                    }

                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    setValuesAndRecalculate({
                        ...kalkulationData,
                        initialized: true,
                        ekAnzahlEinheiten: 1, // artikel.data?.einhProPal || 0,
                        ek1: 0,
                        ek2,
                        vkProz,
                        vkAnzahlEinheiten: 1, // artikel.data?.einhProPal || 0,
                        vk,
                    });
                }
            },
        },
    );

    /*
    const convertPreisByEinheit = React.useCallback(
        (
            valueFrom: number,
            einhFrom?: string,
            einhTo?: string,
        ): {
            value: number;
            factor: number;
        } => {
            if (!einhFrom || !einhTo || !artikel.data) {
                return { value: 0, factor: 0 };
            }

            const einheitFrom = einheitenByCode[einhFrom];
            const einheitTo = einheitenByCode[einhTo];

            if (!einheitFrom || !einheitTo) {
                return { value: 0, factor: 0 };
            }

            if (einhFrom === einhTo) {
                return { value: valueFrom, factor: 1 };
            }

            let factor = 0;
            let valueTo = 0;

            switch (einhFrom) {
                case 'R': {
                    switch (einhTo) {
                        case 'M': // m²
                            factor = artikel.data.quadrmeter;
                            valueTo = valueFrom / factor;
                            break;
                        case 'Y': // 100m²
                            factor = artikel.data.quadrmeter * einheitTo.faktor;
                            valueTo = valueFrom / factor;
                            break;
                        case 'L': // Lfm
                        case 'E': // 100lfm
                        case 'D': // 1.000lfm
                            factor = artikel.data.laufmeter;
                            valueTo = (valueFrom / artikel.data.laufmeter) * einheitTo.faktor;
                            break;
                        case 'S': // Stück
                        case 'U': // Set
                        case 'A': // Satz
                            factor = artikel.data.einhProPal;
                            valueTo = valueFrom / factor;
                            break;
                        case 'T': // 1.000 Stück
                            factor = einheitTo.faktor;
                            valueTo = valueFrom / factor;
                            break;
                        default:
                            console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                            break;
                    }
                    break;
                }
                case 'Y':
                case 'M': {
                    switch (einhTo) {
                        case 'R':
                            factor = artikel.data.quadrmeter * einheitTo.faktor;
                            valueTo = valueFrom * factor;
                            break;
                        default:
                            console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                            break;
                    }
                    break;
                }
                default:
                    console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                    break;
            }

            if (factor === 0) {
                return { value: valueTo, factor: 1 };
            }

            return { value: Math.round(valueTo * 10000) / 10000, factor };
        },
        [artikel, einheitenByCode],
    );
     */

    const setValuesAndRecalculate = React.useCallback(
        ({
            ekAnzahlEinheiten,
            ek1: ek1NotRounded,
            ek2: ek2NotRounded,
            vkAnzahlEinheiten,
            vkProz: vkProzInput,
            einh1Vk: einh1VkInput,
            ...newKalkulationData
        }: KalkulationData) => {
            if (!artikel.data) {
                return;
            }

            const ek1 = Math.round(ek1NotRounded * 10000) / 10000;
            const ek2 = Math.round(ek2NotRounded * 10000) / 10000;

            const { value: ekProEh } = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                ek1,
                kalkulationsblatt?.ekEinh || artikel.data?.einheit || '',
                artikel.data?.einheit || '',
            );

            const ekGesamt = Math.round(ekAnzahlEinheiten * ekProEh * 10000) / 10000;

            const ek1Ek2Diff = Math.round((ek2 - ekProEh) * 10000) / 10000;

            const einh1Ek2 = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                ek2,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh1 || artikel.data?.einheit,
            );
            const einh2Ek2 = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                ek2,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh2,
            );
            const einh3Ek2 = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                ek2,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh3,
            );
            const einh4Ek2 = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                ek2,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh4,
            );

            let vk: number;
            let vkProz: number;

            if (kalkulationData.einh1Vk !== einh1VkInput) {
                vkProz = Math.round(((einh1VkInput - ek2) / (ek2 / 100)) * 10000) / 10000;
                vk = einh1VkInput;
            } else {
                vkProz = vkProzInput;
                vk = Math.round(((ek2 * (100 + vkProzInput)) / 100) * 10000) / 10000;
            }

            const vkGesamt = vk * vkAnzahlEinheiten;

            const bruttoGew = vkGesamt - ekGesamt;

            const einh1Vk = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                vk,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh1 || artikel.data?.einheit,
            );
            const einh2Vk = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                vk,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh2,
            );
            const einh3Vk = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                vk,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh3,
            );
            const einh4Vk = artikelCalculator.convertPreisByEinheit(
                artikel.data,
                vk,
                artikel.data.einheit,
                kalkulationsblatt?.vkEinh4,
            );

            setKalkulationData({
                ...newKalkulationData,
                ekAnzahlEinheiten,
                ek1,
                ekGesamt,
                ekProEh,
                ek2,
                ek1Ek2Diff,
                vkAnzahlEinheiten,
                vkProz,
                vk,
                vkGesamt,
                bruttoGew,
                einh1Ek2: einh1Ek2.value,
                einh1Ek2Factor: einh1Ek2.factor,
                einh2Ek2: einh2Ek2.value,
                einh2Ek2Factor: einh2Ek2.factor,
                einh3Ek2: einh3Ek2.value,
                einh3Ek2Factor: einh3Ek2.factor,
                einh4Ek2: einh4Ek2.value,
                einh4Ek2Factor: einh4Ek2.factor,
                einh1Vk: einh1Vk.value,
                einh1VkFactor: einh1Vk.factor,
                einh2Vk: einh2Vk.value,
                einh2VkFactor: einh2Vk.factor,
                einh3Vk: einh3Vk.value,
                einh3VkFactor: einh3Vk.factor,
                einh4Vk: einh4Vk.value,
                einh4VkFactor: einh4Vk.factor,
            });
        },
        [artikel, artikelCalculator, kalkulationsblatt, kalkulationData.einh1Vk],
    );

    const renderArtikelKalkulationsblatt = () => (
        <KalkulationsblattByBlatt
            label="Kalkulationsblatt"
            disabled
            value={artikel.data?.kalkBlatt || ''}
            onChange={() => {}}
        />
    );

    const renderArtikelEinheit = () => (
        <EinheitByCode label="Artikel-Einheit" disabled value={artikel.data?.einheit || ''} onChange={() => {}} />
    );

    const renderArtikelDecimalField = (column: keyof Artikel, label: string, options: FormDecimalFieldOptions = {}) => (
        <FormDecimalField
            value={(artikel.data?.[column] || 0) as number}
            options={{ ...options, disabled: true }}
            onChange={() => {}}
            label={label}
        />
    );

    const renderArtikelCheckbox = (column: keyof Artikel, label: string, className?: string) => (
        <Checkbox
            className={className || formStyles.checkboxInsideForm}
            label={label}
            disabled
            checked={artikel.data?.[column] as boolean}
            onChange={() => {}}
        />
    );

    const renderEinheit = (einheit?: string, label = '') => (
        <TextField label={label} value={einheit ? einheitenByCode[einheit]?.text || einheit : ''} disabled />
    );

    const renderDecimalField = (column: keyof KalkulationData, label = '') => (
        <FormDecimalField
            label={label}
            value={kalkulationData[column] as number}
            savingState={SavingState.notChanged}
            options={{ disabled: true, fixed: 4 }}
            onChange={() => {}}
        />
    );

    const lieferantLabel = artikelDetailLieferant.data?.lieferant?.nr
        ? (artikelDetailLieferant.data?.lieferant?.name1 || '') +
          ' ' +
          (artikelDetailLieferant.data?.lieferant?.name2 || '') +
          (artikelDetailLieferant.data?.lieferant?.nr ? `(${artikelDetailLieferant.data?.lieferant?.nr})` : '')
        : '';

    return (
        <ConfirmMessage
            title="Neukalkulation"
            minWidth={1100}
            confirmText="Übernehmen"
            cancelText="Abbruch"
            content={
                artikel.isLoading || artikelDetail.isLoading || artikelDetailLieferant.isLoading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <div className={styles.firstLine}>
                            <TextField
                                label="Artikelgruppe"
                                value={artikelGruppeByNr[artikelGruppeNr]?.text}
                                disabled
                            />
                            <TextField label="Artikel-Nr." value={artikelNr} disabled />
                            <TextField label="Bezeichnung" value={artikel.data?.text || ''} disabled />
                            <TextField label="Lieferant" value={lieferantLabel} disabled />
                        </div>

                        <div className={styles.newKalkulationGrid}>
                            <div className={classnames(formStyles.secondColumn, styles.block)}>
                                <div className={styles.blockTitle}>Artikeldaten</div>
                                <div>
                                    <div className={formStyles.gridCell}>{renderArtikelEinheit()}</div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('packEinheit', 'Pack-Einheit', { fixed: 2 })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('einhProPal', 'Einh. je Pal./Kart.', {
                                                fixed: 2,
                                            })}
                                        </div>
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        <div className={formStyles.twoColumnsBlock}>
                                            {renderArtikelCheckbox('bestPal', 'Palette')}
                                            {renderArtikelCheckbox('bestKart', 'Karton')}
                                        </div>
                                    </div>
                                    <div className={formStyles.gridCell}>{renderArtikelKalkulationsblatt()}</div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('staerke', 'Stärke', { fixed: 4 })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('kernId', 'Kern-ID (mm)')}
                                        </div>
                                    </div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('breite', 'Breite (m)', { fixed: 4 })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelCheckbox(
                                                'halbeBreite',
                                                'Halbschlauch',
                                                formStyles.checkboxInsideFormAligned,
                                            )}
                                        </div>
                                    </div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('laufmeter', 'Länge (m)', { fixed: 2 })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('quadrmeter', 'Fläche (m²)', {
                                                fixed: 2,
                                                disabled: true,
                                            })}
                                        </div>
                                    </div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('spezGew', 'Spez. Gew. (kg/dm³)', {
                                                fixed: 4,
                                            })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('kernGew', 'Kerngewicht (kg)', { fixed: 4 })}
                                        </div>
                                    </div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('nettoGew', 'Nettogewicht (kg)', {
                                                fixed: 2,
                                                disabled: true,
                                            })}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderArtikelDecimalField('rollenGew', 'Bruttogewicht (kg)', {
                                                fixed: 2,
                                                disabled: true,
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={classnames(formStyles.thirdColumn, styles.resultColumn)}>
                                <div className={styles.block}>
                                    <div className={styles.blockTitle}>Einkauf 1</div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="Anzahl EH"
                                                value={kalkulationData.ekAnzahlEinheiten}
                                                options={{ fixed: 3 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        ekAnzahlEinheiten: newValue,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(
                                                kalkulationsblatt?.ekEinh || artikel.data?.einheit,
                                                'EK-Einheit',
                                            )}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="EK1"
                                                value={kalkulationData.ek1}
                                                options={{ fixed: 4 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        ek1: newValue,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('ekProEh', 'EK1 pro EH')}
                                        </div>
                                        <div />
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('ekGesamt', 'EK Gesamt')}
                                        </div>

                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="EK2 (n. VK-Einheit!)"
                                                value={kalkulationData.ek2}
                                                options={{ fixed: 4 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        ek2: newValue,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('ek1Ek2Diff', 'EK2 - EK1')}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.block}>
                                    <div className={styles.blockTitle}>Verkauf Grunddaten</div>
                                    <div className={formStyles.twoColumnsBlock}>
                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="Anzahl EH"
                                                value={kalkulationData.vkAnzahlEinheiten}
                                                options={{ fixed: 3 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        vkAnzahlEinheiten: newValue,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(artikel.data?.einheit, 'VK-Einheit')}
                                        </div>

                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="Aufschlag"
                                                value={kalkulationData.vkProz}
                                                options={{ fixed: 4 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        vkProz: newValue,
                                                    })
                                                }
                                            />
                                        </div>

                                        <div className={formStyles.gridCell}>
                                            <FormDecimalField
                                                label="VK / Einheit"
                                                value={kalkulationData.einh1Vk}
                                                options={{ fixed: 4 }}
                                                onChange={(newValue) =>
                                                    setValuesAndRecalculate({
                                                        ...kalkulationData,
                                                        einh1Vk: newValue,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div />
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('vkGesamt', 'VK Gesamt')}
                                        </div>

                                        <div />

                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('bruttoGew', 'Brutto Gew.')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={classnames(formStyles.thirdColumn, styles.resultColumn)}>
                                <div className={styles.block}>
                                    <div className={styles.blockTitle}>Einkauf 2</div>
                                    <div className={classnames(formStyles.threeColumnsBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(
                                                kalkulationsblatt?.vkEinh1 || artikel.data?.einheit || '',
                                                'Einheit',
                                            )}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh1Ek2', 'EK2')}
                                        </div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh1Ek2Factor', 'Faktor')}
                                        </div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh2)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh2Ek2')}</div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh2Ek2Factor')}
                                        </div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh3)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh3Ek2')}</div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh3Ek2Factor')}
                                        </div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh4)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh4Ek2')}</div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh4Ek2Factor')}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.block}>
                                    <div className={styles.blockTitle}>Verkauf</div>
                                    <div className={classnames(formStyles.threeColumnsBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(
                                                kalkulationsblatt?.vkEinh1 || artikel.data?.einheit,
                                                'Einheit',
                                            )}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh1Vk', 'VK')}</div>
                                        <div className={formStyles.gridCell}>
                                            {renderDecimalField('einh1VkFactor', 'Faktor')}
                                        </div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh2)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh2Vk')}</div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh2VkFactor')}</div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh3)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh3Vk')}</div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh3VkFactor')}</div>
                                    </div>
                                    <div className={classnames(formStyles.threeColumnsBlock, styles.secondBlock)}>
                                        <div className={formStyles.gridCell}>
                                            {renderEinheit(kalkulationsblatt?.vkEinh4)}
                                        </div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh4Vk')}</div>
                                        <div className={formStyles.gridCell}>{renderDecimalField('einh4VkFactor')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
            onConfirm={async () => {
                const datum = moment().toISOString();

                const basisArtikelDetailKalkulation: ArtikelDetailKalkulation = {
                    ...defaultArtikelDetailKalkulation,
                    artikelGruppeNr,
                    artikelNr,
                    lNr,
                    lfnr: 1,
                    datum,
                    ek1Eh: kalkulationsblatt?.ekEinh || artikel.data?.einheit || '',
                    ek1: kalkulationData.ek1,
                };

                const artikelDetailKalkulation1: ArtikelDetailKalkulation = {
                    ...basisArtikelDetailKalkulation,
                    lfnr: 1,
                    eh: kalkulationsblatt?.vkEinh1 || artikel.data?.einheit || '',
                    ek2: kalkulationData.einh1Ek2,
                    vkProz: kalkulationData.vkProz,
                    vk: kalkulationData.einh1Vk,
                };

                await addArtikelDetailKalkulation(artikelDetailKalkulation1);

                if (kalkulationsblatt?.vkEinh2) {
                    const artikelDetailKalkulation2: ArtikelDetailKalkulation = {
                        ...basisArtikelDetailKalkulation,
                        lfnr: 2,
                        eh: kalkulationsblatt?.vkEinh2 || '',
                        ek2: kalkulationData.einh2Ek2,
                        vkProz: kalkulationData.vkProz,
                        vk: kalkulationData.einh2Vk,
                    };

                    await addArtikelDetailKalkulation(artikelDetailKalkulation2);
                }

                if (kalkulationsblatt?.vkEinh3) {
                    const artikelDetailKalkulation3: ArtikelDetailKalkulation = {
                        ...basisArtikelDetailKalkulation,
                        lfnr: 3,
                        eh: kalkulationsblatt?.vkEinh3 || '',
                        ek2: kalkulationData.einh3Ek2,
                        vkProz: kalkulationData.vkProz,
                        vk: kalkulationData.einh3Vk,
                    };

                    await addArtikelDetailKalkulation(artikelDetailKalkulation3);
                }

                if (kalkulationsblatt?.vkEinh4) {
                    const artikelDetailKalkulation4: ArtikelDetailKalkulation = {
                        ...basisArtikelDetailKalkulation,
                        lfnr: 4,
                        eh: kalkulationsblatt?.vkEinh4 || '',
                        ek2: kalkulationData.einh4Ek2,
                        vkProz: kalkulationData.vkProz,
                        vk: kalkulationData.einh4Vk,
                    };

                    await addArtikelDetailKalkulation(artikelDetailKalkulation4);
                }

                if (artikelDetailLieferant.data) {
                    await updateArtikelDetailLieferant({
                        ...artikelDetailLieferant.data,
                        einheit: kalkulationsblatt?.vkEinh1 || artikel.data?.einheit || '',
                        ekPreis: kalkulationData.ek2,
                        ekGiltAb: datum,
                        vkPreis: kalkulationData.vk,
                        vkGiltAb: datum,
                    });
                }

                onClose(true);
            }}
            onCancel={() => onClose(false)}
        />
    );
};
